import type { BinId } from './bins';

/** 공모전 "자원순환" 하위주제 ①②③ — 실제 분리배출함(BinId)과는 별개 축이다. */
export type SubTopic = 'reduction_practice' | 'awareness_capacity' | 'circular_economy';

export interface WasteCard {
  id: string;
  /** 인쇄 카드에 넣을 QR 페이로드. 영숫자 모드로 인쇄 손상에 강하게. */
  qrValue: string;
  nameKo: string;
  /** public/cards/ 아래 일러스트 PNG 경로 (앱에서는 img src, 인쇄 시트에서는 base64 임베드). */
  iconRef: string;
  correctBin: BinId;
  commonMistakeNote?: string;
  explanation: string;
  /** public/becomes/ icon shown in the correct-answer transform cutscene, when there's a concrete
   * "reborn as X" outcome to show (shared across cards — most outcomes fall into a few categories). */
  becomesIconRef?: string;
  curriculumCode: string;
  subTopics: SubTopic[];
  /** 정답 시 마을 청정도 점수에 더해지는 값. */
  pollutionDelta: number;
}

export const CARDS: WasteCard[] = [
  {
    id: 'card-01',
    qrValue: 'SUNHWAN:CARD01',
    nameKo: '투명 페트병',
    iconRef: '/cards/card-01.png',
    correctBin: 'plastic',
    commonMistakeNote: '라벨을 떼지 않으면 재활용이 어려워요.',
    explanation: '라벨과 뚜껑을 분리하고 납작하게 눌러서 배출하면 새 옷감·솜으로 다시 태어나요.',
    becomesIconRef: '/becomes/fabric.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-02',
    qrValue: 'SUNHWAN:CARD02',
    nameKo: '배달 플라스틱 용기',
    iconRef: '/cards/card-02.png',
    correctBin: 'plastic',
    commonMistakeNote: '음식물이 묻은 채로 버리면 재활용이 안 돼요.',
    explanation: '헹궈서 배출해야 재활용이 가능해요. 애초에 다회용기를 쓰면 쓰레기 자체가 줄어요.',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-03',
    qrValue: 'SUNHWAN:CARD03',
    nameKo: '우유팩(종이팩)',
    iconRef: '/cards/card-03.png',
    correctBin: 'paper',
    commonMistakeNote: '일반 종이와 섞어 버리면 화장지로 재탄생하기 어려워요.',
    explanation: '종이팩은 일반 종이와 구분해서 배출하면 화장지 등으로 다시 만들어져요.',
    becomesIconRef: '/becomes/paper.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice', 'circular_economy'],
    pollutionDelta: 6,
  },
  {
    id: 'card-04',
    qrValue: 'SUNHWAN:CARD04',
    nameKo: '종이 쇼핑백',
    iconRef: '/cards/card-04.png',
    correctBin: 'paper',
    explanation: '테이프나 손잡이 끈을 떼어내고 배출하면 새 종이 제품으로 재활용돼요.',
    becomesIconRef: '/becomes/paper.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-05',
    qrValue: 'SUNHWAN:CARD05',
    nameKo: '다 쓴 부탄가스캔',
    iconRef: '/cards/card-05.png',
    correctBin: 'canGlass',
    commonMistakeNote: '가스가 남은 채 버리면 수거 과정에서 폭발할 수 있어요.',
    explanation:
      '어른과 함께 환기가 잘 되는 밖에서 남은 가스를 완전히 비운 뒤 배출해요. 구멍은 뚫지 않아도 돼요. 캔은 금속 자원으로 다시 활용돼요.',
    becomesIconRef: '/becomes/metal.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-06',
    qrValue: 'SUNHWAN:CARD06',
    nameKo: '과자봉지 겉비닐',
    iconRef: '/cards/card-06.png',
    correctBin: 'vinyl',
    explanation: '이물질을 털어내고 배출하면 재생 원료로 다시 쓰일 수 있어요.',
    becomesIconRef: '/becomes/plastic-pellets.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-07',
    qrValue: 'SUNHWAN:CARD07',
    nameKo: '코팅 종이컵',
    iconRef: '/cards/card-07.png',
    correctBin: 'general',
    commonMistakeNote: '종이처럼 보여도 안팎이 코팅된 종이컵은 종이 재활용이 안 돼요.',
    explanation: '음료가 새지 않게 안팎을 코팅한 종이컵은 종이류로 재활용되지 않아 일반쓰레기(종량제봉투)로 배출해요. 코팅이 없는 깨끗한 종이컵은 종이팩류로 따로 모으는 곳도 있어요.',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-08',
    qrValue: 'SUNHWAN:CARD08',
    nameKo: '즉석밥 용기',
    iconRef: '/cards/card-08.png',
    correctBin: 'general',
    commonMistakeNote: '플라스틱처럼 보이지만 여러 재질이 겹쳐 있어 재활용이 안 돼요.',
    explanation:
      '즉석밥 용기는 밥을 오래 보관하려고 산소를 막는 특수 재질을 겹쳐 만든 복합재질(OTHER)이라, 재활용이 안 돼서 일반쓰레기로 배출해요.',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-09',
    qrValue: 'SUNHWAN:CARD09',
    nameKo: '영수증',
    iconRef: '/cards/card-09.png',
    correctBin: 'general',
    commonMistakeNote: '종이처럼 보이지만 특수 코팅이 있어 종이 재활용이 안 돼요.',
    explanation: '영수증 종이(감열지)는 열을 가하면 글씨가 나타나도록 화학 코팅이 되어 있어서, 일반 종이와 달리 일반쓰레기로 배출해요.',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-10',
    qrValue: 'SUNHWAN:CARD10',
    nameKo: '깨진 유리컵',
    iconRef: '/cards/card-10.png',
    correctBin: 'general',
    commonMistakeNote: '멀쩡한 유리병과 달리 깨진 유리는 재활용이 안 돼요.',
    explanation: '신문지 등으로 감싸 다치지 않게 표시한 뒤 일반쓰레기로 배출해요.',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-11',
    qrValue: 'SUNHWAN:CARD11',
    nameKo: '은박 코팅 과자봉지',
    iconRef: '/cards/card-11.png',
    correctBin: 'vinyl',
    commonMistakeNote: '반짝이는 은박이라 캔이나 일반쓰레기로 착각하기 쉬워요.',
    explanation:
      '속이 은박인 과자·라면 봉지도 깨끗하게 비우면 비닐류로 배출해요. 기름이나 음식물이 묻어 씻기 어려울 때만 일반쓰레기예요.',
    becomesIconRef: '/becomes/plastic-pellets.png',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-12',
    qrValue: 'SUNHWAN:CARD12',
    nameKo: '라벨 안 뗀 페트병',
    iconRef: '/cards/card-12.png',
    correctBin: 'plastic',
    commonMistakeNote: '라벨을 안 떼면 재활용 등급이 낮아져요.',
    explanation: '라벨을 떼고 배출해야 더 좋은 재활용 재료가 돼요. 배출함은 맞지만 손질이 먼저예요.',
    becomesIconRef: '/becomes/plastic-pellets.png',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-13',
    qrValue: 'SUNHWAN:CARD13',
    nameKo: '다 쓴 건전지',
    iconRef: '/cards/card-13.png',
    correctBin: 'special',
    commonMistakeNote: '일반쓰레기로 버리면 중금속이 토양을 오염시켜요.',
    explanation: '주민센터나 아파트 관리실의 폐건전지 수거함에 배출하면 금속 자원을 회수할 수 있어요.',
    becomesIconRef: '/becomes/metal.png',
    curriculumCode: '6실02-11',
    subTopics: ['circular_economy'],
    pollutionDelta: 8,
  },
  {
    id: 'card-14',
    qrValue: 'SUNHWAN:CARD14',
    nameKo: '다 쓴 형광등',
    iconRef: '/cards/card-14.png',
    correctBin: 'special',
    commonMistakeNote: '유리병과 함께 버리면 안 돼요. 소량의 수은이 들어 있어요.',
    explanation: '폐형광등 전용수거함에 배출해야 안전하게 처리하고 자원을 회수할 수 있어요.',
    curriculumCode: '6실02-11',
    subTopics: ['circular_economy'],
    pollutionDelta: 8,
  },
  {
    id: 'card-15',
    qrValue: 'SUNHWAN:CARD15',
    nameKo: '낡은 티셔츠',
    iconRef: '/cards/card-15.png',
    correctBin: 'special',
    explanation: '의류수거함에 넣으면 다른 사람이 다시 입는 옷이나 새활용(업사이클) 재료로 순환돼요.',
    becomesIconRef: '/becomes/fabric.png',
    curriculumCode: '6실02-10',
    subTopics: ['circular_economy'],
    pollutionDelta: 7,
  },
  {
    id: 'card-16',
    qrValue: 'SUNHWAN:CARD16',
    nameKo: '알루미늄 음료수 캔',
    iconRef: '/cards/card-16.png',
    correctBin: 'canGlass',
    commonMistakeNote: '내용물이 남아 있으면 다른 재활용품까지 오염시켜요.',
    explanation:
      '내용물을 비우고 가볍게 찌그러뜨려 배출하면, 캔은 녹여서 다시 새 캔으로 몇 번이고 재활용되는 대표적인 순환 자원이에요.',
    becomesIconRef: '/becomes/metal.png',
    curriculumCode: '6실02-10',
    subTopics: ['circular_economy'],
    pollutionDelta: 7,
  },
  {
    id: 'card-17',
    qrValue: 'SUNHWAN:CARD17',
    nameKo: '안 쓰는 휴대폰',
    iconRef: '/cards/card-17.png',
    correctBin: 'special',
    commonMistakeNote: '일반쓰레기로 버리면 안에 있는 귀금속을 잃어버려요.',
    explanation:
      '소형 폐가전(전자제품) 전용수거함에 배출하면, 서울에서는 SR센터(서울도시금속회수센터)로 모여 금·은 같은 희귀금속을 다시 회수해요. 그래서 이런 곳을 "도시광산"이라고 불러요.',
    becomesIconRef: '/becomes/precious-metal.png',
    curriculumCode: '6실02-11',
    subTopics: ['circular_economy'],
    pollutionDelta: 8,
  },
  {
    id: 'card-18',
    qrValue: 'SUNHWAN:CARD18',
    nameKo: '다 쓴 칫솔',
    iconRef: '/cards/card-18.png',
    correctBin: 'general',
    commonMistakeNote: '플라스틱처럼 보여도 손잡이와 칫솔모 재질이 섞여 재활용이 안 돼요.',
    explanation:
      '칫솔은 플라스틱 손잡이에 나일론 솔이 박힌 복합재질이라 재활용이 안 돼서 일반쓰레기(종량제봉투)로 배출해요.',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
  {
    id: 'card-19',
    qrValue: 'SUNHWAN:CARD19',
    nameKo: '유리 잼병',
    iconRef: '/cards/card-19.png',
    correctBin: 'canGlass',
    commonMistakeNote: '뚜껑을 분리하지 않으면 재질이 섞여 선별이 어려워요.',
    explanation: '내용물을 비우고 뚜껑을 분리해서 배출하면 새 유리병으로 다시 태어나요.',
    becomesIconRef: '/becomes/glass.png',
    curriculumCode: '6실02-10',
    subTopics: ['reduction_practice'],
    pollutionDelta: 6,
  },
  {
    id: 'card-20',
    qrValue: 'SUNHWAN:CARD20',
    nameKo: '위생팩(지퍼백)',
    iconRef: '/cards/card-20.png',
    correctBin: 'vinyl',
    commonMistakeNote: '음식물이 남아 있으면 재활용이 어려워요.',
    explanation: '깨끗이 씻어 말린 뒤 배출하면 재생 비닐 원료로 다시 쓰일 수 있어요.',
    becomesIconRef: '/becomes/plastic-pellets.png',
    curriculumCode: '6실02-11',
    subTopics: ['awareness_capacity'],
    pollutionDelta: 6,
  },
];
